import { AlertAction } from "@components/alert/Alert";
import YearSelector from "@components/inputs/date/YearSelector";
import { IInputOnChangeEvent } from "@components/inputs/input";
import ReadOnlyList from "@components/readOnlyList/ReadOnlyList";
import { IReadOnlyListItem } from "@components/readOnlyList/ReadOnlyListItem";
import { IRow, TId } from "@components/table";
import { ODataError } from "@odata/Data.types";
import {
    EntitySetName,
    IPrEmployeeEntity,
    IPrTaxpayerDeclarationEntity,
    PrTaxpayerDeclarationEntity
} from "@odata/GeneratedEntityTypes";
import { PrTaxpayerDeclarationStatusCode } from "@odata/GeneratedEnums";
import { isBatchResultOk } from "@odata/OData";
import { massExportToPDF } from "@pages/payroll/taxpayerDeclaration/TaxpayerDeclaration.utils";
import { getValue } from "@utils/general";
import React, { lazy, ReactElement, Suspense } from "react";

import { RowAction, Status, ToolbarItemType } from "../../../enums";
import { ITableStorageDefaultCustomData } from "../../../model/TableStorage";
import { getUtcDate, getUtcDayjs } from "../../../types/Date";
import { getAlertFromError } from "../../../views/formView/Form.utils";
import { SmartHeaderStyled } from "../../../views/formView/FormView.styles";
import TableView from "../../../views/table";
import {
    ICustomButtonDefArgs,
    overrideMassExportButtonDef,
    TableActionOrder,
    TableButtonsAction,
    TableButtonsActionType,
    TTableToolbarItem
} from "../../../views/table/TableToolbar.utils";
import { IHandleCustomActionArgs, ITableViewBaseProps, ITableViewBaseState } from "../../../views/table/TableView";
import { getConfirmationActionText } from "../../../views/table/TableView.render.utils";

const TaxpayerDeclarationMassCreateDialog = lazy(() => import("@pages/payroll/taxpayerDeclaration/taxpayerDeclarationMassCreateDialog/taxpayerDeclarationMassCreateDialog"));

interface IDeclarationCustomData extends ITableStorageDefaultCustomData {
    selectedYear?: number;
    declarationDates?: Date[];
}

interface IProps extends ITableViewBaseProps<IDeclarationCustomData> {
    isSingleEmployeeView: boolean;
}

interface IState extends ITableViewBaseState {
    isMassCreateDialogOpen: boolean;
}

const MASS_CREATE_ACTION = "massCreate";
const MASS_SIGN_ACTION = "massSign";
const MASS_SEND_TO_SIGN_ACTION = "massSendToSign";

class TaxpayerDeclarationTableView extends TableView<IProps, IState> {

    constructor(props: IProps) {
        super(props);

        this.overrideDefaultToolbarButtonDef = this.overrideDefaultToolbarButtonDef.bind(this);
    }

    get shouldRenderBreadcrumbs(): boolean {
        return !this.props.isSingleEmployeeView;
    }

    get secondaryTitle(): string {
        if (this.props.isSingleEmployeeView) {
            return this.props.storage.t("TaxpayerDeclaration:Table.SecondaryTitle");
        }
        return null;
    }

    get headerData(): IReadOnlyListItem[] {
        if (!this.props.isSingleEmployeeView) {
            return [];
        }

        const rootStorage = this.props.storage.getCustomData()?.rootStorage;
        const employee = rootStorage?.data.entity as IPrEmployeeEntity;

        return [{
            label: this.props.storage.t("TaxpayerDeclaration:Table.EmployeeFirstName"),
            value: employee?.FirstName
        }, {
            label: this.props.storage.t("TaxpayerDeclaration:Table.EmployeeLastName"),
            value: employee?.LastName
        }, {
            label: this.props.storage.t("TaxpayerDeclaration:Table.EmployeeNumber"),
            value: employee?.NumberOurs
        }];
    }

    onBeforeFetch = async (): Promise<void> => {
        if (this.props.isSingleEmployeeView) {
            return;
        }

        const res = await this.props.storage.oData.getEntitySetWrapper(EntitySetName.PrTaxpayerDeclarations).query()
                .groupBy(PrTaxpayerDeclarationEntity.Year)
                .fetchData<IPrTaxpayerDeclarationEntity[]>();
        const declarations: IPrTaxpayerDeclarationEntity[] = res.value;

        if (!declarations.length) {
            return;
        }

        const sortedDeclarations = declarations.sort((a, b) => a.Year - b.Year);

        const declarationDates = sortedDeclarations.map((declaration) => {
            return getUtcDayjs().set("year", declaration.Year).toDate();
        });

        const thisYear = getUtcDate().getFullYear();
        this.setDeclarationDateFilter(thisYear);

        this.props.storage.setCustomData({
            selectedYear: thisYear,
            declarationDates
        });
    };

    setDeclarationDateFilter = (year: number): void => {
        const filter = `${PrTaxpayerDeclarationEntity.Year} eq ${year}`;
        this.props.storage.store({ customSecondaryFilter: filter });
    };

    getToolbarButtons(): TableButtonsActionType[] {
        const buttons = super.getToolbarButtons();
        if (this.props.isSingleEmployeeView) {
            return buttons;
        }
        return [
            ...buttons,
            TableButtonsAction.MassExport,
            MASS_CREATE_ACTION,
            MASS_SEND_TO_SIGN_ACTION,
            MASS_SIGN_ACTION
        ];
    }

    get hasTabs(): boolean {
        return !this.props.isSingleEmployeeView;
    }

    getConfirmText = (tableAction: TableButtonsAction | string): string | React.ReactElement => {
        const { storage } = this.props;

        const activeRowActionCount = storage.tableAPI?.getState().activeRows.size ?? 0;
        let key: string;

        switch (tableAction) {
            case MASS_SEND_TO_SIGN_ACTION:
                key = "Send";
                break;
            case MASS_SIGN_ACTION:
                key = "Sign";
                break;
        }
        if (key) {
            return getConfirmationActionText(storage.t(`TaxpayerDeclaration:Table.${key}`), activeRowActionCount);
        }

        return super.getConfirmText(tableAction);
    };

    overrideDefaultToolbarButtonDef(defaultItem: TTableToolbarItem): TTableToolbarItem {
        return overrideMassExportButtonDef(defaultItem, this.props.storage.t("TaxpayerDeclaration:MassExportPdf.MassExportActionHeader"));
    }

    getCustomButtonDef = (type: TableButtonsActionType, {
        isActive,
        isDisabled
    }: ICustomButtonDefArgs): TTableToolbarItem => {
        const isTableEmpty = !this.props.storage.tableAPI?.getState().rowCount;

        if (type === MASS_CREATE_ACTION) {
            return {
                id: MASS_CREATE_ACTION,
                itemType: ToolbarItemType.Icon,
                iconName: "MassCreate",
                label: this.props.storage.t("TaxpayerDeclaration:Table.MassCreate"),
                order: TableActionOrder.Add + 1,
                isActive,
                isDisabled: (isDisabled && !isActive)
            };
        }

        if (type === MASS_SEND_TO_SIGN_ACTION) {
            return {
                id: MASS_SEND_TO_SIGN_ACTION,
                itemType: ToolbarItemType.Icon,
                iconName: "MassSendToSign",
                label: this.props.storage.t("TaxpayerDeclaration:Table.MassSendToSign"),
                order: TableActionOrder.Add + 2,
                isActive,
                isDisabled: (isDisabled && !isActive) || isTableEmpty
            };
        }

        if (type === MASS_SIGN_ACTION) {
            return {
                id: MASS_SIGN_ACTION,
                itemType: ToolbarItemType.Icon,
                iconName: "MassSign",
                label: this.props.storage.t("TaxpayerDeclaration:Table.MassSign"),
                order: TableActionOrder.Add + 3,
                isActive,
                isDisabled: (isDisabled && !isActive) || isTableEmpty
            };
        }

        return null;
    };

    isRowWithoutAction(rowId: TId, action: RowAction, row: IRow): boolean {
        if (action === RowAction.Custom) {
            const statusCode = (row.customData.entity as IPrTaxpayerDeclarationEntity).StatusCode as PrTaxpayerDeclarationStatusCode;
            if (this.getTableAction() === MASS_SIGN_ACTION) {
                return ![PrTaxpayerDeclarationStatusCode.WaitingForSignature, PrTaxpayerDeclarationStatusCode.Draft].includes(statusCode);
            } else if (this.getTableAction() === MASS_SEND_TO_SIGN_ACTION) {
                return ![PrTaxpayerDeclarationStatusCode.Draft].includes(statusCode);
            }
        }

        return super.isRowWithoutAction(rowId, action, row);
    }

    handleMassSendToSign = async (): Promise<void> => {
        await this.handleStatusChange(PrTaxpayerDeclarationStatusCode.WaitingForSignature);
    };

    handleMassSign = async (): Promise<void> => {
        await this.handleStatusChange(PrTaxpayerDeclarationStatusCode.Signed);
    };

    handleMassExport = (ids: string[]): Promise<void> => {
        return massExportToPDF(ids, this.props.storage.context);
    }

    handleMassCreate = async (): Promise<void> => {
        this.setState({
            isMassCreateDialogOpen: true
        });
        this.forceUpdate();
    };

    handleMassCreateDialogConfirm = async (): Promise<void> => {
        const { storage } = this.props;
        await this.handleMassCreateDialogClose();
        storage.setTableAlert({
            status: Status.Success,
            title: storage.t("Components:Table.UpdateOk"),
            subTitle: storage.t("TaxpayerDeclaration:Table.MassCreateSuccess"),
            useFade: true
        });
        await storage.tableAPI.reloadTable();
    };

    handleMassCreateDialogClose = async (): Promise<void> => {
        this.setState({
            isMassCreateDialogOpen: false
        });
        await this.setTableAction(null);
    };

    handleStatusChange = async (statusCode: PrTaxpayerDeclarationStatusCode): Promise<void> => {
        const { storage } = this.props;
        const selectedRows = await storage.tableAPI.getAffectedRows();

        const batch = storage.oData.batch();
        batch.beginAtomicityGroup("massSign");

        for (const row of selectedRows) {
            batch.getEntitySetWrapper(EntitySetName.PrTaxpayerDeclarations)
                    .update(row.key, {
                        [PrTaxpayerDeclarationEntity.StatusCode]: statusCode
                    });
        }

        let errors: string[] = [];
        const batchResponse = await batch.execute();

        for (const res of batchResponse) {
            if (!isBatchResultOk(res)) {
                if (res.body instanceof ODataError || typeof res.body === "string") {
                    const resSubtitle = getAlertFromError(res.body).subTitle as string[];
                    errors = [...errors, ...resSubtitle];
                }
            }
        }

        if (errors.length) {
            storage.data.alert = {
                action: AlertAction.Close,
                status: Status.Error,
                useFade: false,
                title: storage.t("Common:Errors.ErrorHappened"),
                subTitle: errors?.join("\n")
            };
        } else {
            storage.data.alert = {
                status: Status.Success,
                title: storage.t("Components:Table.UpdateOk"),
                subTitle: storage.t(`TaxpayerDeclaration:Table.${statusCode}Success`),
                useFade: true
            };
        }

        this.setTableAction(null);
        await this.props.storage.tableAPI.reloadTable();
    };

    handleCustomAction(action: string, args: IHandleCustomActionArgs = {}): void {
        if (action === MASS_CREATE_ACTION) {
            this.handleMassCreate();
        } else {
            super.handleCustomAction(action, args);
        }
    }

    async handleToolbarConfirm(): Promise<void> {
        if (this.isTableAction(MASS_SIGN_ACTION)) {
            return this.handleMassSign();
        }
        if (this.isTableAction(MASS_SEND_TO_SIGN_ACTION)) {
            return this.handleMassSendToSign();
        }
        return super.handleToolbarConfirm();
    }

    handleYearChange = (e: IInputOnChangeEvent<Date>): void => {
        const year = getUtcDayjs(e.value).get("year");
        this.setDeclarationDateFilter(year);
        this.props.storage.setCustomData({ selectedYear: year });
        this.forceUpdate();
    };

    renderTabs(): ReactElement {
        const storage = this.props.storage;
        const dates = storage.getCustomData().declarationDates ?? [];
        const value = this.props.storage.getCustomData().selectedYear;
        if (!value) {
            return null;
        }
        return <YearSelector
                value={getUtcDayjs().set("year", value).toDate()}
                onChange={this.handleYearChange}
                minDate={dates[0] ?? getUtcDayjs().subtract(1, "year").toDate()}
                isMain={true}
        />;
    }

    renderSmartHeader(): React.ReactElement {
        return (<>
            <ReadOnlyList title={this.secondaryTitle}
                          data={getValue(this.headerData ?? [])}
            />
            <SmartHeaderStyled
                    storage={this.props.storage}
                    title={this.props.storage.data.definition.title}
                    hotspotId={"tableHeader"}
                    shouldHideVariant={!!this.props.storage.data.predefinedFilters || this.props.storage.data.definition.preventStoreVariant}/>
        </>);
    }


    renderMassCreateDialog = (): React.ReactElement => {
        return (
                <Suspense fallback={<></>}>
                    <TaxpayerDeclarationMassCreateDialog
                            onConfirm={this.handleMassCreateDialogConfirm}
                            onCancel={this.handleMassCreateDialogClose}
                            rootStorage={this.props.storage}
                    />
                </Suspense>
        );
    };

    render() {
        return (
                <>
                    {this.state.isMassCreateDialogOpen && this.renderMassCreateDialog()}
                    {super.render()}
                </>
        );
    }
}

export default TaxpayerDeclarationTableView;